import { useState } from 'react';
import type {
  EditPatientAllergyRequestT,
  PatientAllergyT,
  ReactionTypeT,
  StatusTypeT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useEditPatientAllergyMutation } from '@/entities/rx';

export interface AllergyEditDraft {
  reaction: string;
  reactionType: ReactionTypeT;
  statusType: StatusTypeT;
  onsetDate: string;
}

export interface UseAllergyEditingResult {
  editMutation: ReturnType<typeof useEditPatientAllergyMutation>;
  editError: string | null;

  editingAllergyId: string | null;
  draft: AllergyEditDraft | null;

  startEditing: (allergy: PatientAllergyT) => void;
  cancelEditing: () => void;
  setDraft: (next: AllergyEditDraft) => void;
  save: () => Promise<void>;
}

function toDraft(allergy: PatientAllergyT): AllergyEditDraft {
  return {
    reaction: allergy.Reaction?.trim() ?? '',
    reactionType: (allergy.ReactionType ?? 'Allergy') as ReactionTypeT,
    statusType: (allergy.StatusType ?? 'Active') as StatusTypeT,
    onsetDate: allergy.OnsetDate?.trim() ?? '',
  };
}

function toRequest(draft: AllergyEditDraft): EditPatientAllergyRequestT {
  const reaction = draft.reaction.trim();
  const onsetDate = draft.onsetDate.trim();

  return {
    ReactionType: draft.reactionType,
    StatusType: draft.statusType,
    ...(reaction.length > 0 ? { Reaction: reaction } : { Reaction: null }),
    ...(onsetDate.length > 0 ? { OnsetDate: onsetDate } : { OnsetDate: null }),
  };
}

export function useAllergyEditing(userId: string): UseAllergyEditingResult {
  const editMutation = useEditPatientAllergyMutation();
  const [editError, setEditError] = useState<string | null>(null);
  const [editingAllergyId, setEditingAllergyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AllergyEditDraft | null>(null);

  const startEditing = (allergy: PatientAllergyT) => {
    const id = allergy.PatientAllergyId;
    if (id == null) {
      setEditError('This allergy cannot be edited (missing id).');
      return;
    }
    setEditError(null);
    setEditingAllergyId(String(id));
    setDraft(toDraft(allergy));
  };

  const cancelEditing = () => {
    setEditError(null);
    setEditingAllergyId(null);
    setDraft(null);
  };

  const save = async () => {
    setEditError(null);

    if (!editingAllergyId || !draft) {
      setEditError('Nothing to save.');
      return;
    }

    try {
      await editMutation.mutateAsync({
        userId,
        allergyId: editingAllergyId,
        data: toRequest(draft),
      });
      cancelEditing();
    } catch {
      setEditError('Could not save allergy changes. Please try again.');
    }
  };

  return {
    editMutation,
    editError,
    editingAllergyId,
    draft,
    startEditing,
    cancelEditing,
    setDraft,
    save,
  };
}
